import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import {
  Table,
  Space,
  Select,
  Button,
  Modal,
  Row,
  Col,
  Form,
  DatePicker,
  Divider,
  Typography,
  Input,
  Dropdown,
  Menu,
  Upload,
  InputNumber,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import AvatarEditor from "react-avatar-editor";
import "quill/dist/quill.snow.css";
import { UseGetRequest, UsePostRequest } from "../../utils/api/ApiRequests";
import {
  GetAllEmployees,
  GetAllEmploymentStatus,
  GetAllPositions,
  GetAllProjects,
  GetAllSalaryTypes,
} from "../../utils/api/DropdownFiltersApi";
import { usePostRequest } from "../../hooks/usePostRequest";
import {
  capitalizeEachWord,
  getIsAdminLogin,
  getEmployeeId,
  refreshPage,
} from "../../utils/Common";
import Compressor from "compressorjs";

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    let validationTimeout;

    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    validationTimeout = setTimeout(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setSubmittable(true))
        .catch(() => setSubmittable(false));
    }, 300);

    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [values]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  );
};

const AddSubmitMedicalButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  const formValues = Form.useWatch([], form);

  React.useEffect(() => {
    if (formValues) {
      const isValid = Object.keys(formValues).map((row) => {
        if (formValues[row].date && formValues[row].date !== "") {
          return true;
        } else {
          return false;
        }
      });
      const hasTrueValue = isValid.some((value) => value === true);
      setSubmittable(hasTrueValue);
    }
  }, [formValues]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  );
};

const { Title } = Typography;

export default function Employee() {
  const [form] = Form.useForm();
  const [pictureForm] = Form.useForm();
  const [filesForm] = Form.useForm();
  const [medicalForm] = Form.useForm();
  const editorRef = useRef();
  const navigate = useNavigate();
  const isAdminLogin = getIsAdminLogin();
  const employeeId = getEmployeeId();
  const [modal, contextHolder] = Modal.useModal();

  const [tableData, setTableData] = useState([]);
  const [excelUrl, setExcelUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmploymentInfo, setShowEmploymentInfo] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const [showUploadFileModal, setShowUploadFileModal] = useState(false);
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);
  const [showUpdateMedical, setShowUpdateMedical] = useState(false);
  const [showUploadExitDocs, setShowUploadExitDocs] = useState(false);
  const [clickedRowId, setClickedRowId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [photoList, setPhotoList] = useState([]);
  const defaultMedicalRecord = [
    { type: "CBC", key: "cbc", date: null },
    { type: "Fasting Blood Sugar", key: "fasting_blood_sugar", date: null },
    { type: "Drug Test", key: "drug_test", date: null },
    { type: "Chext Xray", key: "chest_xray", date: null },
    { type: "ECG", key: "ecg", date: null },
    { type: "Spirometry", key: "spirometry", date: null },
    { type: "Audiometry", key: "audiometry", date: null },
    { type: "Medical Certificate", key: "medical_certificate", date: null },
    { type: "Physical Exam", key: "physical_exam", date: null },
  ];
  const [medicalData, setMedicalData] = useState(defaultMedicalRecord);
  const requirementKeys = [
    { key: "valid_id", label: "Valid ID(s)" },
    { key: "nbi_clearance", label: "NBI Clearance" },
    { key: "police_clearance", label: "Police Clearance" },
    { key: "work_certification", label: "Work Certifications" },
    { key: "training_certification", label: "Training Certifications" },
    { key: "bond", label: "Bonds" },
    { key: "contract", label: "Contract" },
  ];
  const defaultFiles = {
    valid_id: [],
    nbi_clearance: [],
    police_clearance: [],
    work_certification: [],
    training_certification: [],
    bond: [],
    contract: [],
    valid_id_size: false,
    nbi_clearance_size: false,
    police_clearance_size: false,
    work_certification_size: false,
    training_certification_size: false,
    bond_size: false,
    contract_size: false,
  };
  const [requirements, setRequirements] = useState(defaultFiles);
  const exitDocumentKeys = [
    { key: "release", label: "Release and Quit Claim" },
    { key: "clearance", label: "Clearance" },
    { key: "coe", label: "COE" },
  ];
  const defaultExitDocs = {
    release: [],
    clearance: [],
    coe: [],
    reset_quit_claim_size: false,
    clearance_size: false,
    coe_size: false,
  };
  const [exitDocuments, setExitDocuments] = useState(defaultExitDocs);
  const [zoomLevel, setZoomLevel] = useState(1);

  const statusOptions = GetAllEmploymentStatus();
  const positionOptions = GetAllPositions();
  const projectOptions = GetAllProjects();
  const salaryTypeOptions = GetAllSalaryTypes();
  const employeeOptions = GetAllEmployees();
  const dateFormat = "YYYY-MM-DD";
  const [oldSalary, setOldSalary] = useState();
  const [oldPosition, setOldPosition] = useState();

  const { callPost, isLoading } = usePostRequest(() => {});

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    employee_id: isAdminLogin ? null : employeeId,
  });

  const tableColumns = [
    {
      title: "DTR ID",
      dataIndex: "dtr_id",
      key: "dtr_id",
    },
    {
      title: "Employee",
      dataIndex: "fullname",
      key: "fullname",
      render: (fullname) => <div>{capitalizeEachWord(fullname)}</div>,
    },
    {
      title: "Date Hired",
      dataIndex: "date_hired",
      key: "date_hired",
      render: (date_hired, record) => (
        <div>{date_hired ? moment(date_hired).format("MMM DD, YYYY") : ""}</div>
      ),
    },
    {
      title: "Date Regularized",
      dataIndex: "date_regularized",
      key: "date_regularized",
      render: (date_regularized, record) => (
        <div>
          {date_regularized
            ? moment(date_regularized).format("MMM DD, YYYY")
            : ""}
        </div>
      ),
    },
    {
      title: "End of Contract",
      dataIndex: "end_of_contract",
      key: "end_of_contract",
      render: (end_of_contract, record) => (
        <div>
          {end_of_contract
            ? moment(end_of_contract).format("MMM DD, YYYY")
            : ""}
        </div>
      ),
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Actions",
      width: "10%",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                onClick={() => {
                  handleRowClick(record);
                  setShowEditModal(true);
                }}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                key="contract"
                onClick={() => {
                  handleRowClick(record);
                  setShowEmploymentInfo(true);
                }}
              >
                Update Employment Info
              </Menu.Item>
              <Menu.Item
                key="medical"
                onClick={() => {
                  setClickedRowId(record.id);
                  fetchMedicalRecord(record.id);
                }}
              >
                Update Medical Record
              </Menu.Item>
              <Menu.Item
                key="updateProfile"
                onClick={() => {
                  setClickedRowId(record);
                  setShowUpdateProfile(true);
                }}
              >
                Update Profile Picture
              </Menu.Item>
              <Menu.Item
                key="upload"
                onClick={() => getEmployeeRequirements(record)}
              >
                Upload Requirements
              </Menu.Item>
              <Menu.Item
                key="exit-docs"
                onClick={() => getEmployeeExitDocs(record)}
              >
                Upload Exit Documents
              </Menu.Item>
              <Menu.Item
                key="history"
                onClick={() => navigate("history-report", { state: record })}
              >
                View History Report
              </Menu.Item>
              <Menu.Item
                key="profile"
                onClick={
                  () => navigate("view/" + record.id)
                  // window.open("/employees/view/" + record.id, "_blank")
                }
              >
                View Profile
              </Menu.Item>
              <Menu.Item
                key="delete"
                className="danger"
                onClick={() => confirmDelete(record)}
              >
                Delete
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button
            className="ant-dropdown-link"
            onClick={(e) => e.preventDefault()}
          >
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  function postCallPostAction() {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowEmploymentInfo(false);
    setShowUploadFileModal(false);
    setShowUpdateProfile(false);
    setShowUpdateProfile(false);
    setShowUpdateMedical(false);
    setUploadedImage(null);
    resetValues();
    fetchTableData();
  }

  const onFinish = (formInputs) => {
    // filter out empty object and child object where name and age are empty
    const filteredChildrenArray = formInputs.childrenArray.filter((child) => {
      return (
        Object.keys(child).length > 0 &&
        child.child_name &&
        child.child_name.trim() !== "" &&
        child.child_age &&
        child.child_age !== ""
      );
    });
    const childLen = filteredChildrenArray.length;
    formInputs.no_of_children = childLen;
    formInputs.children =
      childLen > 0 ? filteredChildrenArray.map((item) => item.child_name) : [];
    formInputs.age =
      childLen > 0 ? filteredChildrenArray.map((item) => item.child_age) : [];
    formInputs.occupation =
      childLen > 0
        ? filteredChildrenArray.map((item) => item.child_occupation)
        : [];
    formInputs.birthdate = formInputs.birthdate?.format(dateFormat) || "";
    formInputs.date_hired = formInputs.date_hired?.format(dateFormat) || "";
    formInputs.end_of_contract =
      formInputs.end_of_contract?.format(dateFormat) || "";
    formInputs.date_regularized =
      formInputs.date_regularized?.format(dateFormat) || "";
    formInputs.project_ids = formInputs.projectArray.map(
      (item) => item.project_id
    );
    formInputs.project_allowance = formInputs.projectArray.map(
      (item) => item.project_allowance
    );

    // Send empty string values to undefined fields (these are fields with no input at all)
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key] === undefined) {
        formInputs[key] = "";
      }
    });
    callPost("employees/add", formInputs, false);
    setTimeout(() => {
      postCallPostAction();
    }, 500);
  };

  const onEdit = (formInputs) => {
    const filteredChildrenArray = formInputs.childrenArray.filter((child) => {
      return (
        Object.keys(child).length > 0 &&
        child.child_name &&
        child.child_name.trim() !== "" &&
        child.child_age &&
        child.child_age !== ""
      );
    });
    const childLen = filteredChildrenArray.length;
    formInputs.no_of_children = childLen;
    formInputs.children =
      childLen > 0 ? filteredChildrenArray.map((item) => item.child_name) : [];
    formInputs.age =
      childLen > 0 ? filteredChildrenArray.map((item) => item.child_age) : [];
    formInputs.occupation =
      childLen > 0
        ? filteredChildrenArray.map((item) => item.child_occupation)
        : [];

    formInputs.birthdate = formInputs.birthdate
      ? formInputs.birthdate.format(dateFormat)
      : "";
    formInputs.date_hired = formInputs.date_hired
      ? formInputs.date_hired.format(dateFormat)
      : "";
    formInputs.end_of_contract = formInputs.end_of_contract
      ? formInputs.end_of_contract.format(dateFormat)
      : "";
    formInputs.date_regularized = formInputs.date_regularized
      ? formInputs.date_regularized.format(dateFormat)
      : "";
    formInputs.salary = formInputs.basic_salary;

    formInputs.project_ids = formInputs.projectArray.map(
      (item) => item.project_id
    );
    formInputs.project_allowance = formInputs.projectArray.map(
      (item) => item.project_allowance
    );

    // Set undefined values into empty strings
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key] === undefined || formInputs[key] === null) {
        formInputs[key] = "";
      }
    });

    
    callPost(`employees/update/` + clickedRowId, formInputs, false);

    // Update salary if & only if basic salary is edited
    if (
      Number(parseFloat(formInputs.basic_salary).toFixed(2)) !==
      Number(parseFloat(oldSalary).toFixed(2))
    ) {
      const salaryValues = {
        employee_id: clickedRowId,
        salary_from: oldSalary,
        basic_salary: formInputs.basic_salary,
        salary_type: formInputs.salary_type,
      };
      callPost("employees/update_employee_salary", salaryValues, false);
    }

    // Update position if & only if position is edited
    if (
      parseFloat(formInputs.position_id).toFixed(2) !==
      parseFloat(oldPosition).toFixed(2)
    ) {
      const positionValues = {
        employee_id: clickedRowId,
        position_from: oldSalary,
        position_id: formInputs.position_id,
      };
      callPost("employees/update_employee_position", positionValues, false);
    }

    setTimeout(() => {
      postCallPostAction();
    }, 500);
  };

  const createFileFromDataUrl = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch && mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleUpdatePicture = async (formInputs) => {
    if (editorRef.current) {
      const editedImage = editorRef.current
        .getImageScaledToCanvas()
        .toDataURL();
      const imageFile = createFileFromDataUrl(editedImage, uploadedImage.name);

      const compressedImage = await new Promise((resolve, reject) => {
        new Compressor(imageFile, {
          quality: 0.6,
          success(result) {
            resolve(result);
          },
          error(err) {
            reject(err);
          },
        });
      });

      const tempImages = {
        employee_id: clickedRowId.id,
        profile_pic: compressedImage,
      };
      const finalValues = Object.entries(tempImages).map(([key, value]) => ({
        key,
        value,
      }));
      callPost("employees/upload_profile", finalValues, true);
      setTimeout(() => {
        postCallPostAction();
      }, 500);
    }
  };

  // Function to call API for uploading requirements
  const handleUploadRequirements = (formInputs) => {
    const values = {
      employee_id: clickedRowId,
      valid_file: [],
      valid_file_id: [],
      nbi_file: [],
      nbi_file_id: [],
      police_file: [],
      police_file_id: [],
      work_certification: [],
      work_certification_id: [],
      training_certification: [],
      training_certification_id: [],
      bond: [],
      bond_id: [],
      contract: [],
      contract_id: [],
    };

    const emptyFile = new File([""], "", { type: "application/octet-stream" });

    (formInputs.valid_id ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.valid_file.push(file.originFileObj);
        values.valid_file_id.push("");
      } else {
        values.valid_file.push(emptyFile);
        values.valid_file_id.push(file.uid);
      }
    });

    (formInputs.nbi_clearance ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.nbi_file.push(file.originFileObj);
        values.nbi_file_id.push("");
      } else {
        values.nbi_file.push(emptyFile);
        values.nbi_file_id.push(file.uid);
      }
    });

    (formInputs.police_clearance ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.police_file.push(file.originFileObj);
        values.police_file_id.push("");
      } else {
        values.police_file.push(emptyFile);
        values.police_file_id.push(file.uid);
      }
    });

    (formInputs.work_certification ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.work_certification.push(file.originFileObj);
        values.work_certification_id.push("");
      } else {
        values.work_certification.push(emptyFile);
        values.work_certification_id.push(file.uid);
      }
    });

    (formInputs.training_certification ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.training_certification.push(file.originFileObj);
        values.training_certification_id.push("");
      } else {
        values.training_certification.push(emptyFile);
        values.training_certification_id.push(file.uid);
      }
    });

    (formInputs.bond ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.bond.push(file.originFileObj);
        values.bond_id.push("");
      } else {
        values.bond.push(emptyFile);
        values.bond_id.push(file.uid);
      }
    });
    (formInputs.contract ?? []).forEach((file) => {
      if (file.originFileObj) {
        values.contract.push(file.originFileObj);
        values.contract_id.push("");
      } else {
        values.contract.push(emptyFile);
        values.contract_id.push(file.uid);
      }
    });

    const finalValues = Object.entries(values).map(([key, value]) => ({
      key,
      value,
    }));

    callPost("employees/upload_requirements", finalValues, true);
    setTimeout(() => {
      refreshPage();
    }, 1500);
  };

  // Function to upload exit document files
  const handleUploadExitDocs = (formInputs) => {
    const values = {
      employee_id: clickedRowId,
      release: [],
      release_id: [],
      clearance: [],
      clearance_id: [],
      coe: [],
      coe_id: [],
    };

    const emptyFile = new File([""], "", { type: "application/octet-stream" });
    exitDocumentKeys.forEach((req) => {
      (formInputs[req.key] ?? []).forEach((file) => {
        if (file.originFileObj) {
          values[req.key].push(file.originFileObj);
          values[`${req.key}_id`].push("");
        } else {
          values[req.key].push(emptyFile);
          values[`${req.key}_id`].push(file.uid);
        }
      });
    })

    const finalValues = Object.entries(values).map(([key, value]) => ({
      key,
      value,
    }));

    callPost("employees/upload_exit_docs", finalValues, true);
    setTimeout(() => {
      refreshPage();
    }, 1000);
  };

  const handleUpdateMedical = (formInputs) => {
    let objectifiedValues = {};
    Object.keys(formInputs).forEach((row) => {
      objectifiedValues[formInputs[row].key] = formInputs[row].date
        ? formInputs[row].date.format("MM-DD")
        : null;
    });
    callPost(
      "employees/update_medical_record",
      { ...objectifiedValues, employee_id: clickedRowId },
      false
    );
    setTimeout(() => {
      postCallPostAction();
    }, 500);
  };

  const confirmDelete = (employee) => {
    modal.confirm({
      title: `Are you sure you want to delete employee "${employee.last_name}"?`,
      icon: <ExclamationCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => handleDelete(employee.id),
      okType: "danger",
    });
  };

  function handleDelete(passedId) {
    callPost("employees/delete", { employee_id: passedId }, false);
    setTimeout(() => {
      postCallPostAction();
    }, 500);
  }

  // Function to resent employee CRUD forms
  function resetValues() {
    form.resetFields();
    pictureForm.resetFields();
    medicalForm.resetFields();
    setMedicalData(defaultMedicalRecord);
  }

  // Function to resent attachment Form and resent file arrays
  function resetFiles() {
    filesForm.resetFields();
    setRequirements(defaultFiles);
    setExitDocuments(defaultExitDocs);
    setShowUploadFileModal(false);

    setTimeout(() => {
      setShowUploadFileModal(false);
      // refreshPage();
    }, 500);
  }

  // Before upload function for profile picture
  const beforeUploadProfile = (file) => {
    const isSizeValid = file.size / 1024 / 1024 < 1; // Check if file size is less than 1MB (backend request)
    if (!isSizeValid) {
      setFileSizeExceeded(true);
      return Upload.LIST_IGNORE
    } else {
      setFileSizeExceeded(false);
      setUploadedImage(file);
      setPhotoList([...photoList, file]);
    }
    return false
  };

  // Before upload function for uploading requirements
  const beforeUploadRequirements = (file, field) => {
    const isSizeValid = file.size / 1024 / 1024 < 2; // Check if file size is less than 1MB (backend request)
    if (!isSizeValid) {
      setRequirements((prev) => ({ ...prev, [`${field}_size`]: true }));
      return Upload.LIST_IGNORE
    } else {
      setRequirements((prev) => ({ ...prev, [`${field}_size`]: false }));
      setRequirements((prev) => ({ ...prev, [field]: [...prev[field], file] }));
    }
    return false
  };

  // Before upload function for uploading file attachments
    const beforeUploadExitDocs = (file, field) => {
      const isSizeValid = file.size / 1024 / 1024 < 2; // Check if file size is less than 1MB (backend request)
      if (!isSizeValid) {
        setExitDocuments((prev) => ({ ...prev, [`${field}_size`]: true }));
        return Upload.LIST_IGNORE
      } else {
        setExitDocuments((prev) => ({ ...prev, [`${field}_size`]: false }));
        setExitDocuments((prev) => ({
          ...prev,
          [field]: [...prev[field], file],
        }));
      }
      return false
    };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleZoomChange = (value) => {
    setZoomLevel(value);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }));
    }
  };

  const handleFilter = (e, field) => {
    setFilterConfig((prev) => ({ ...prev, [field]: e }));
  };

  const handleRowClick = (rowData) => {
    setClickedRowId(rowData.id);
    getEmployeeDetailsById(rowData.id);
  };

  function formatDayjs(data, format) {
    if (data) return dayjs(data, format);
    return "";
  }

  // function validateMedicalForms () {
  //   const formValues = medicalForm.getFieldsValue();
  //   Object.keys(formValues).forEach((row) => {
  //     if (formValues[row] && formValues[row]!=="") {
  //       setSubmittableMedForm(true)
  //     }
  //   })
  //   setSubmittableMedForm(false)
  // }

  async function getEmployeeDetailsById(passedId) {
    const response = await UsePostRequest("employees/get", {
      employee_id: passedId,
    });
    if (response.data) {
      const res = response.data[0];
      // For the major form -- edit, update contract, allowance, etc.
      const formValues = res;
      formValues.birthdate = res.birthdate
        ? dayjs(res.birthdate, dateFormat)
        : "";
      formValues.date_hired = res.date_hired
        ? dayjs(res.date_hired, dateFormat)
        : "";
      formValues.date_rehired = res.date_rehired
        ? dayjs(res.date_rehired, dateFormat)
        : "";
      formValues.date_resigned_terminated = res.date_resigned_terminated
        ? dayjs(res.date_resigned_terminated, dateFormat)
        : "";
      formValues.end_of_contract = res.end_of_contract
        ? dayjs(res.end_of_contract, dateFormat)
        : "";
      formValues.date_regularized = res.date_regularized
        ? dayjs(res.date_regularized, dateFormat)
        : "";
      formValues.salary_type = res.salary_type;
      formValues.basic_salary = Number(parseFloat(res.basic_salary).toFixed(2));
      setOldSalary(res.basic_salary);
      setOldPosition(res.position_id);
      formValues.ecola_allowance = parseFloat(res.ecola_allowance).toFixed(2);
      let tempChildren = [];

      if (res.children && res.children.length > 0) {
        tempChildren = res.children.map((item) => {
          let info = {};
          info.child_name = item.name;
          info.child_age = item.age;
          info.child_occupation = item.occupation;
          return info;
        });
      }
      formValues.childrenArray = tempChildren;
      formValues.educ_descriptions = res.employee_educ_background
        ? res.employee_educ_background.map((item) => item.description)
        : [];
      formValues.work_descriptions = res.employee_work_background
        ? res.employee_work_background.map((item) => item.description)
        : [];
      formValues.projectArray = (res.projects ?? []).map((row) => {
        return {
          project_id: row.project_id,
          project_allowance: parseFloat(row.project_allowance).toFixed(2),
        };
      });

      form.setFieldsValue(formValues);
    }
  }

  async function getEmployeeRequirements(rowData) {
    setClickedRowId(rowData.id);
    const response = await UsePostRequest(
      "employees/get_employee_requirements",
      { employee_id: rowData.id }
    );
    if (response.data) {
      const res = response.data[0];
      // Function to populate fileList for the Ant Design Upload component
      const updatedFileList = {
        valid_id: res.valid_id
          ? res.valid_id.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
        nbi_clearance: res.nbi_clearance
          ? res.nbi_clearance.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
        police_clearance: res.police_clearance
          ? res.police_clearance.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
        training_certification: res.training_certification
          ? res.training_certification.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
        work_certification: res.work_certification
          ? res.work_certification.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
        bond: res.bonds
          ? res.bonds.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
          contract: res.contract
          ? res.contract.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
      };
      setRequirements(updatedFileList);
    } else {
      setRequirements(defaultFiles);
    }
    setTimeout(() => {
      setShowUploadFileModal(true);
    }, 500);
  }

  async function getEmployeeExitDocs(rowData) {
    setClickedRowId(rowData.id);
    const response = await UsePostRequest(
      "employees/get_employee_exit_docs",
      { employee_id: rowData.id }
    );
    
    if (response.data) {
      const res = response.data[0]
      const updatedFileList = {
        release: res.release
          ? res.release.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
          clearance: res.clearance
          ? res.clearance.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
          coe: res.coe
          ? res.coe.map((file) => {
              return {
                uid: file.id,
                name: file.file_name,
                status: "done",
                url: file.file_url,
                fileType: file.file_type,
              };
            })
          : [],
      }
      setExitDocuments(updatedFileList)
    } else {
      setExitDocuments(defaultExitDocs)
    }
    setShowUploadExitDocs(true);
  }

  async function fetchMedicalRecord(passedId) {
    const response = await UseGetRequest("employees/get_medical_record", {
      employee_id: passedId,
    });
    if (response.data) {
      const res = response.data.medical_record[0];
      const tempArray = [
        { type: "CBC", key: "cbc", date: formatDayjs(res.cbc, "MM-DD") },
        {
          type: "Fasting Blood Sugar",
          key: "fasting_blood_sugar",
          date: formatDayjs(res.fasting_blood_sugar, "MM-DD"),
        },
        {
          type: "Drug Test",
          key: "drug_test",
          date: formatDayjs(res.drug_test, "MM-DD"),
        },
        {
          type: "Chext Xray",
          key: "chest_xray",
          date: formatDayjs(res.chest_xray, "MM-DD"),
        },
        { type: "ECG", key: "ecg", date: formatDayjs(res.ecg, "MM-DD") },
        {
          type: "Spirometry",
          key: "spirometry",
          date: formatDayjs(res.spirometry, "MM-DD"),
        },
        {
          type: "Audiometry",
          key: "audiometry",
          date: formatDayjs(res.audiometry, "MM-DD"),
        },
        {
          type: "Medical Certificate",
          key: "medical_certificate",
          date: formatDayjs(res.medical_certificate, "MM-DD"),
        },
        {
          type: "Physical Exam",
          key: "physical_exam",
          date: formatDayjs(res.physical_exam, "MM-DD"),
        },
      ];
      const transformedObject = {};

      tempArray.forEach((item, index) => {
        transformedObject[index] = item;
      });

      medicalForm.setFieldsValue(transformedObject);
      setMedicalData(tempArray);
    } else {
      setMedicalData(defaultMedicalRecord);
    }
    setShowUpdateMedical(true);
  }

  async function fetchTableData() {
    setIsFetching(true);
    setTableData([]);
    const response = await UsePostRequest("employees/get_all", filterConfig);
    if (response.data) {
      const res = response.data.map((data) => {
        const info = data;
        info.fullname = `${data.last_name}, ${data.first_name} ${data.middle_name}`;
        return info;
      });
      setTableData(res);
    }
    setIsFetching(false);

    const excelResponse = await UsePostRequest("employees/create_employee_excel", filterConfig);
    if (excelResponse.response) {
      setExcelUrl(excelResponse.response.url)
    } else {
      setExcelUrl(null)
    }
    
  }

  React.useEffect(() => {
    fetchTableData();
  }, [filterConfig]);

  React.useEffect(() => {
    renderFileForm();
  }, [requirements]);

  // React.useEffect(() => {
  //   validateMedicalForms()
  // }, [medicalForm])

  // These functions are the different input field sections in every modal in the employee module
  // Decided to put them as functions since I will be reusing them across the code
  // For which section every function is should be self explanatory.
  // Why do I need to reuse them in every modal? Because for some reason, if these sections are not included in the Form,
  // their values disappear sa form (which I need by the way even if I only edit one section at once)

  function formProfileSection() {
    return (
      <>
        <Divider />
        <Row>
          <Title level={5}>Personal Information</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="first_name"
              label="First Name"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="middle_name"
              label="Middle Name"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="nickname" label="Nickname">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="contact_no"
              label="Contact Number"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Input type="" maxLength={11} minLength={11} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="address"
              label="Address"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Select
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="birthdate"
              label="Birthdate"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="civil_status"
              label="Civil Status"
              rules={[
                {
                  required: true, whitespace: true,
                },
              ]}
            >
              <Select
                options={[
                  { value: "single", label: "Single" },
                  { value: "married", label: "Married" },
                  { value: "widowed", label: "Widowed" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="nationality"
              label="Nationality"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="religion" label="Religion">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Row>
          <Title level={5}>Benefit Contribution Information</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="sss"
              label="SSS Number"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="hdmf"
              label="HDMF Number"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="philhealth"
              label="PhilHealth Number"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="sss_ee_amount"
              label="SSS EE Amount"
              rules={[{ required: true, }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="sss_er_amount"
              label="SSS ER Amount"
              rules={[{ required: true, }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()}/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="hdmf_ee_amount"
              label="HDMF EE Amount"
              rules={[{ required: true, }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()}/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="hdmf_er_amount"
              label="HDMF ER Amount"
              rules={[{ required: true, }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="philhealth_ee_amount"
              label="PhilHealth EE Amount"
              rules={[{ required: true, }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()}/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="philhealth_er_amount"
              label="PhilHealth ER Amount"
              rules={[{ required: true }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="ec_amount"
              label="EC Amount"
              rules={[{ required: true }]}
              initialValue={0}
            >
              <Input type="number" onWheel={(e)=>e.target.blur()} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="is_generali"
              label="HMO"
              rules={[{ required: true, whitespace: true }]}
            >
              <Select
                options={[
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="is_gpa"
              label="GPA"
              rules={[{ required: true, whitespace: true }]}
            >
              <Select
                options={[
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Row>
          <Title level={5}>Withholding Tax Information</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="tin"
              label="TIN Number"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="wtaxstatus" label="Status">
              <Select
                options={[
                  { value: "Zero Exception", label: "Zero Exception" },
                  { value: "Single", label: "Single" },
                  { value: "Married", label: "Married" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="dependent" label="Dependent">
              <Input onWheel={(e)=>e.target.blur()} type="number" />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Row>
          <Title level={5}>Family Relationship Information</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="spouse_name" label="Spouse Name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={4} md={4} lg={4}>
            <Form.Item name="spouse_age" label="Age">
              <Input onWheel={(e)=>e.target.blur()} type="number" />
            </Form.Item>
          </Col>
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="spouse_occupation" label="Occupation">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="mothers_name" label="Mother's Name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={4} md={4} lg={4}>
            <Form.Item name="mothers_age" label="Age">
              <Input onWheel={(e)=>e.target.blur()} type="number" />
            </Form.Item>
          </Col>
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="mothers_occupation" label="Occupation">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="fathers_name" label="Father's Name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={4} md={4} lg={4}>
            <Form.Item name="fathers_age" label="Age">
              <Input onWheel={(e)=>e.target.blur()} type="number" />
            </Form.Item>
          </Col>
          <Col xs={10} md={10} lg={10}>
            <Form.Item name="fathers_occupation" label="Occupation">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Form.List name="childrenArray" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                <Col xs={24} lg={24}>
                  {fields.map(({ key, name, fieldKey }) => (
                    <Row
                      key={key}
                      style={{ marginBottom: 0 }}
                      className="d-flex justify-content-between"
                    >
                      <Col xs={10}>
                        <Form.Item
                          name={[name, "child_name"]}
                          label="Child Name"
                          fieldKey={[fieldKey, "child_name"]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={4}>
                        <Form.Item
                          label="Age"
                          name={[name, "child_age"]}
                          fieldKey={[fieldKey, "child_age"]}
                        >
                          <Input onWheel={(e)=>e.target.blur()} type="number" />
                        </Form.Item>
                      </Col>
                      <Col xs={8}>
                        <Form.Item
                          label="Occupation"
                          name={[name, "child_occupation"]}
                          fieldKey={[fieldKey, "child_occupation"]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={2} className="mt-4 p-1">
                        <Button
                          onClick={() => {
                            remove(name);
                          }}
                          icon={<DeleteOutlined />}
                        />
                      </Col>
                    </Row>
                  ))}
                </Col>
                <Row>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusCircleOutlined />}
                  >
                    Add Child
                  </Button>
                </Row>
              </>
            )}
          </Form.List>
        </Row>

        <Divider />
        <Row>
          <Title level={5}>Person to Contact in Case of Emergency</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={12} lg={12}>
            <Form.Item
              name="contact_person"
              label="Contact Person"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={12}>
            <Form.Item
              name="contact_person_relationship"
              label="Relationship to Contact Person"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={12}>
            <Form.Item
              name="contact_person_no"
              label="Contact Number"
              rules={[{ required: false }]}
            >
              <Input type="" maxLength={11} minLength={11} />
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={12}>
            <Form.Item
              name="contact_person_address"
              label="Contact Address"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Row>
          <Title level={5}>Background</Title>
        </Row>
        <Row>
          <Col xs={24} md={24} className="my-2">
            Educational Background
            <Row>
              <Form.List name="educ_descriptions" initialValue={[]}>
                {(fields, { add, remove }) => (
                  <Row className="w-100 m-0">
                    {fields.map(({ key, name }) => (
                      <>
                        <Col xs={20} className="pb-0-force">
                          <Form.Item name={[name]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={2} className="">
                          <Button
                            onClick={() => {
                              remove(name);
                            }}
                            icon={<DeleteOutlined />}
                          />
                        </Col>
                      </>
                    ))}
                    <Col xs={6} className="m-0">
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusCircleOutlined />}
                      >
                        Add
                      </Button>
                    </Col>
                  </Row>
                )}
              </Form.List>
            </Row>
          </Col>
          <Col xs={24} md={24} className="my-2">
            Previous Work
            <Row>
              <Form.List name="work_descriptions" initialValue={[]}>
                {(fields, { add, remove }) => (
                  <Row className="w-100">
                    {fields.map(({ key, name }) => (
                      <>
                        <Col xs={20} className="pb-0-force">
                          <Form.Item name={[name]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={2} className="">
                          <Button
                            onClick={() => {
                              remove(name);
                            }}
                            icon={<DeleteOutlined />}
                          />
                        </Col>
                      </>
                    ))}
                    <Col xs={6}>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusCircleOutlined />}
                      >
                        Add
                      </Button>
                    </Col>
                  </Row>
                )}
              </Form.List>
            </Row>
          </Col>
        </Row>
      </>
    );
  }

  function formEmploymentInfoSection() {
    return (
      <>
        <Divider />
        <Row>
          <Title level={5}>Employment Information</Title>
        </Row>
        <Row className="mt-2">
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="dtr_id"
              label="DTR ID"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="employment_status_id"
              label="Employment Status"
              rules={[{ required: true, whitespace: true }]}
            >
              <Select options={statusOptions.slice(1)} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="salary_type"
              label="Salary Type"
              rules={[{ required: true, whitespace: true }]}
            >
              <Select options={salaryTypeOptions.slice(1)} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="basic_salary"
              label="Basic Salary"
              rules={[{ required: true }]}
            >
              <InputNumber onWheel={(e)=>e.target.blur()} min={0} className="no-arrows"/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="bank" label="Bank">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="bank_account_name" label="Bank Account Name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="bank_account_no" label="Bank Account Number">
              <Input onWheel={(e)=>e.target.blur()} type="number" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="immediate_head_id" label="Immediate Head">
              <Select
                showSearch
                placeholder="Select employee"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                filterSort={(optionA, optionB) => {
                  if (optionA.label?.includes("All")) return -1;
                  if (optionA.label?.includes("All")) return 1;
                  return optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase());
                }}
                options={employeeOptions.slice(1)}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="position_id"
              label="Position"
              rules={[{ required: true, whitespace: true }]}
            >
              <Select options={positionOptions.slice(1)} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="date_hired"
              label="Date Hired"
              rules={[{ required: true}]}
            >
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="end_of_contract" label="End of Contract">
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>

          <Col xs={12} md={8} lg={8}>
            <Form.Item name="date_regularized" label="Date Regularized">
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item name="date_rehired" label="Date Rehired">
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="date_resigned_terminated"
              label="Date Terminated/Resigned"
            >
              <DatePicker placeholder="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="ecola_allowance"
              label="ECOLA Allowance"
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber onWheel={(e)=>e.target.blur()} min={0}/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="vacation_leave_credits"
              label="Vacation Leave Credits"
              initialValue={0}
              rules={[{ required: true }]}
            >
              <InputNumber onWheel={(e)=>e.target.blur()} min={0}/>
            </Form.Item>
          </Col>
          <Col xs={12} md={8} lg={8}>
            <Form.Item
              name="sick_leave_credits"
              label="Sick Leave Credits"
              initialValue={0}
              rules={[{ required: true}]}
            >
              <InputNumber onWheel={(e)=>e.target.blur()} min={0}/>
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={24}>
            <Divider />
            <Title level={5}>Stores</Title>
            <Row>
              <Form.List name="projectArray" initialValue={[{}]}>
                {(fields, { add, remove }) => (
                  <>
                    <Col xs={24} lg={20}>
                      {fields.map(({ key, name, fieldKey }) => (
                        <Row
                          key={key}
                          style={{ marginBottom: 0 }}
                          className="d-flex justify-content-between"
                        >
                          <Col xs={15} className="pb-0-force">
                            <Form.Item
                              name={[name, "project_id"]}
                              label="Store"
                              fieldKey={[fieldKey, "project_id"]}
                              rules={[{ required: true, whitespace: true }]}
                            >
                              <Select options={projectOptions.slice(1)} />
                            </Form.Item>
                          </Col>
                          <Col xs={7} className="pb-0-force">
                            <Form.Item
                              label="Meal Allowance"
                              name={[name, "project_allowance"]}
                              fieldKey={[fieldKey, "project_allowance"]}
                              initialValue={0}
                              rules={[{ required: true }]}
                            >
                              <InputNumber onWheel={(e)=>e.target.blur()} min={0}/>
                            </Form.Item>
                          </Col>
                          <Col xs={2} className="mt-4 p-1 pb-0-force">
                            <Button
                              onClick={() => {
                                remove(name);
                              }}
                              icon={<DeleteOutlined />}
                            />
                          </Col>
                        </Row>
                      ))}
                    </Col>
                    <Row>
                      <Col xs={12}>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusCircleOutlined />}
                      >
                        Add Store
                      </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </Form.List>
            </Row>
          </Col>
        </Row>
      </>
    );
  }

  function renderFileForm() {
    return (
      <Form form={filesForm} layout="vertical" onFinish={handleUploadRequirements}>
        <Row className="w-100 mt-3">
          {requirementKeys.map((req, index) => (
            <Col xs={24} md={12} className="mt-2" key={index}>
              <Form.Item
                name={req.key}
                label={req.label}
                valuePropName="fileList"
                getValueFromEvent={normFile}
                tooltip="File must not exceed 1MB"
                initialValue={requirements[req.key]}
              >
                <Upload
                  name={req.key}
                  listType="list"
                  multiple={true}
                  onRemove={(file) => {
                    const index = requirements[req.key].indexOf(file);
                    const newFileList = requirements[req.key].slice();
                    newFileList.splice(index, 1);
                    setRequirements((prev) => ({
                      ...prev,
                      [req.key]: newFileList,
                    }));
                  }}
                  beforeUpload={(file) =>
                    beforeUploadRequirements(file, req.key)
                  }
                  fileList={requirements[req.key]}
                  showUploadList={true}
                  accept=".png,.jpeg,.jpg,.pdf"
                >
                  <Button icon={<UploadOutlined />}>Upload File</Button>
                </Upload>
                {requirements[`${req.key}_size`] && (
                  <div className="ant-form-item-explain-error">
                    File must not exceed 1MB
                  </div>
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Form.Item>
          <Row className="d-flex justify-content-end mt-3">
            <Button
              danger
              onClick={() => {
                resetFiles();
              }}
              disabled={isLoading}
              className="me-2"
            >
              Cancel
            </Button>
            <AddSubmitButton form={filesForm} isLoading={isLoading} />
          </Row>
        </Form.Item>
      </Form>
    );
  }

  // const actionDropdownOptions = [
  //   {
  //     value: "add",
  //     label: "Add Employee",
  //     action: () => setShowAddModal(true),
  //   },
  //   {
  //     value: "daily",
  //     label: (
  //       <a href={excelUrl} className="pointer no-style-btn">
  //         Export Masterlist
  //       </a>
  //     ),
  //   },
  // ];
  
  function handleExportExcel () {
    if (excelUrl) {
      const link = document.createElement('a');
      link.href = excelUrl;
      link.download = 'ExmployeeMasterlist.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  return (
    <Navbar
    activeNav="employee"
      pageTitle="Employees"
      pageButtonTitle="Add Employee"
      onClickButton={() => {
        setShowAddModal(true);
      }}
      pageDownloadButtonTitle="Export Masterlist" 
      isDownloadBtn
      onClickDownloadButton = {handleExportExcel}
      // noButton
      // actionDropdownButton
      // actionDropdownOptions={actionDropdownOptions}
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      searchPlaceholder="employee"
      isManager
      searchable
    >
      <Space className="mt-3">
        <Select
          showSearch
          style={{ width: 200 }}
          placeholder="Employment Status"
          optionFilterProp="items"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input?.toLowerCase())
          }
          filterSort={(optionA, optionB) => {
            if (optionA.label?.includes("All")) return -1;
            if (optionB.label?.includes("All")) return 1;
            return optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase());
          }}
          options={statusOptions}
          onChange={(e) => handleFilter(e, "employment_status_id")}
        />
        <Select
          showSearch
          style={{ width: 200 }}
          placeholder="Position"
          optionFilterProp="items"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input?.toLowerCase())
          }
          filterSort={(optionA, optionB) => {
            if (optionA.label?.includes("All")) return -1;
            if (optionB.label?.includes("All")) return 1;
            return optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase());
          }}
          options={positionOptions}
          onChange={(e) => handleFilter(e, "position_id")}
        />
        <Select
          showSearch
          style={{ width: 200 }}
          placeholder="Store"
          optionFilterProp="items"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input?.toLowerCase())
          }
          filterSort={(optionA, optionB) => {
            if (optionA.label?.includes("All")) return -1;
            if (optionB.label?.includes("All")) return 1;
            return optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase());
          }}
          options={projectOptions}
          onChange={(e) => handleFilter(e, "project_id")}
        />
        <Select
          showSearch
          style={{ width: 200 }}
          placeholder="Salary Type"
          optionFilterProp="items"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input?.toLowerCase())
          }
          filterSort={(optionA, optionB) => {
            if (optionA.label?.includes("All")) return -1;
            if (optionB.label?.includes("All")) return 1;
            return optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase());
          }}
          options={salaryTypeOptions}
          onChange={(e) => handleFilter(e, "salary_type")}
        />
        <Select
          style={{ width: 150 }}
          placeholder="HMO"
          optionFilterProp="items"
          options={[
            {value: "1", label: "Yes"},
            {value: "0", label: "No"},
          ]}
          onChange={(e) => handleFilter(e, "is_generali")}
        />
        <Select
          style={{ width: 150 }}
          placeholder="GPA"
          optionFilterProp="items"
          options={[
            {value: "1", label: "Yes"},
            {value: "0", label: "No"},
          ]}
          onChange={(e) => handleFilter(e, "is_gpa")}
        />
      </Space>
      <Table
        className="mt-2"
        rowKey="id"
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching}
      />

      {/* ADD MODAL */}
      <Modal
        title="Add Employee"
        centered
        open={showAddModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetValues();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={onFinish}
        >
          {formProfileSection()}
          {formEmploymentInfoSection()}

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        title="Edit Employee"
        centered
        open={showEditModal}
        footer={null}
        onCancel={() => {
          setShowEditModal(false);
          resetValues();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={onEdit}
        >
          {formProfileSection()}

          {/* these are hidden but cannot be removed because the values for some reason disappears from the form */}
          <div style={{ display: "none" }}>{formEmploymentInfoSection()}</div>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowEditModal(false);
                    resetValues();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* UPDATE EMPLOYMENT INFO MODAL */}
      <Modal
        title="Update Employment Information"
        centered
        open={showEmploymentInfo}
        footer={null}
        onCancel={() => {
          setShowEmploymentInfo(false);
          resetValues();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={onEdit}
        >
          {formEmploymentInfoSection()}

          <div style={{ display: "none" }}>{formProfileSection()}</div>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowEmploymentInfo(false);
                    resetValues();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* UPDATE PROFILE PICTURE */}
      <Modal
        title="Update Profile Picture"
        centered
        open={showUpdateProfile}
        footer={null}
        onCancel={() => {
          setShowUpdateProfile(false);
          setUploadedImage(null);
        }}
      >
        <Form
          className="mt-3 mx-3"
          form={pictureForm}
          name="validateOnly"
          layout="vertical"
          onFinish={handleUpdatePicture}
        >
          <Form.Item
            name="images"
            label="Photo"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            tooltip="Image must be of size 1000 x 250 px."
            rules={[{ required: true, whitespace: true }]}
          >
            <Upload
              name="profile_picture"
              listType="picture"
              multiple={false}
              maxCount={1}
              onRemove={(file) => {
                const index = photoList.indexOf(file);
                const newFileList = photoList.slice();
                newFileList.splice(index, 1);
                setPhotoList(newFileList);
                setUploadedImage();
              }}
              beforeUpload={beforeUploadProfile}
              fileList={photoList}
              showUploadList={false}
              accept=".png, .jpg, .jpeg"
            >
              <Button icon={<UploadOutlined />}>Upload image</Button>
            </Upload>
            {fileSizeExceeded && (
              <div className="ant-form-item-explain-error">
                File must not exceed 2MB
              </div>
            )}
          </Form.Item>
          {uploadedImage && (
            <>
              <Row className="justify-content-center">
                <AvatarEditor
                  ref={editorRef}
                  image={uploadedImage}
                  width={300}
                  height={350}
                  border={5}
                  scale={zoomLevel}
                  className="avatar-editor-image"
                />
              </Row>
              <Row className="d-flex justify-content-center mt-2">
                <input
                  className="w-75"
                  type="range"
                  min="0.2"
                  max="3"
                  step="0.1"
                  value={zoomLevel}
                  onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                />
              </Row>
            </>
          )}

          <Form.Item>
            <Row className="d-flex justify-content-between mt-3">
              <Button
                danger
                onClick={() => {
                  setUploadedImage(null);
                  setShowUpdateProfile(false);
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <AddSubmitButton form={pictureForm} isLoading={isLoading} />
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* UPDATE requirements */}
      <Modal
        title="Upload Files"
        centered
        open={showUploadFileModal}
        footer={null}
        onCancel={() => {
          resetFiles();
        }}
        width={800}
      >
        {renderFileForm()}
      </Modal>

      {/* Update Medical Record Modal */}
      <Modal
        title="Update Medical Record"
        centered
        open={showUpdateMedical}
        footer={false}
        onCancel={() => {
          setShowUpdateMedical(false);
          resetValues();
        }}
      >
        <Form
          form={medicalForm}
          layout="vertical"
          onFinish={handleUpdateMedical}
        >
          <Table
            className="medical-table"
            dataSource={medicalData}
            columns={[
              {
                title: "Type",
                dataIndex: "type",
                key: "type",
                render: (data, record, index) => (
                  <div>
                    <Form.Item name={[index, "type"]} initialValue={data}>
                      <Input disabled />
                    </Form.Item>
                  </div>
                ),
              },
              {
                title: "Date",
                dataIndex: "date",
                key: "date",
                render: (data, record, index) => (
                  <div>
                    <Form.Item name={[index, "date"]} initialValue={data}>
                      <DatePicker format="MM-DD" />
                    </Form.Item>
                  </div>
                ),
              },
              {
                title: "",
                dataIndex: "key",
                key: "key",
                width: "0",
                render: (data, record, index) => (
                  <div>
                    <Form.Item
                      name={[index, "key"]}
                      initialValue={data}
                    ></Form.Item>
                  </div>
                ),
              },
            ]}
            pagination={false}
          />
          <Form.Item>
            <Row className="d-flex justify-content-end mt-3">
              <Button
                danger
                onClick={() => {
                  setShowUpdateMedical(false);
                  resetValues();
                }}
                disabled={isLoading}
                className="me-2"
              >
                Cancel
              </Button>
              <AddSubmitMedicalButton
                form={medicalForm}
                isLoading={isLoading}
              />
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {/* UPLOAD EXIT DOCUMENTS */}
      <Modal
        title="Upload Exit Documents"
        centered
        open={showUploadExitDocs}
        footer={null}
        onCancel={() => {
          filesForm.resetFields();
          setShowUploadExitDocs(false);
          setExitDocuments(defaultExitDocs);
        }}
      >
        <div className="py-3 px-2">
          <Form
            form={filesForm}
            layout="vertical"
            onFinish={handleUploadExitDocs}
          >
            <Row className="w-100">
              {exitDocumentKeys.map((req, index) => (
                <Col xs={24} className="mt-2" key={index}>
                  <Form.Item
                    name={req.key}
                    label={req.label}
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    tooltip="File must not exceed 1MB"
                    initialValue={exitDocuments[req.key]}
                  >
                    <Upload
                      name={req.key}
                      listType="list"
                      multiple={true}
                      onRemove={(file) => {
                        const index = exitDocuments[req.key].indexOf(file);
                        const newFileList = exitDocuments[req.key].slice();
                        newFileList.splice(index, 1);
                        setExitDocuments((prev) => ({
                          ...prev,
                          [req.key]: newFileList,
                        }));
                      }}
                      beforeUpload={(file) =>
                        beforeUploadExitDocs(file, req.key)
                      }
                      fileList={exitDocuments[req.key]}
                      showUploadList={true}
                      accept=".png,.jpeg,.jpg,.pdf"
                    >
                      <Button icon={<UploadOutlined />}>Upload File</Button>
                    </Upload>
                    {exitDocuments[`${req.key}_size`] && (
                      <div className="ant-form-item-explain-error">
                        File must not exceed 1MB
                      </div>
                    )}
                  </Form.Item>
                </Col>
              ))}
            </Row>

            <Form.Item>
              <Row className="d-flex justify-content-end mt-3">
                <Button
                  danger
                  onClick={() => {
                    filesForm.resetFields();
                    setShowUploadExitDocs(false);
                    setExitDocuments(defaultExitDocs);
                  }}
                  disabled={isLoading}
                  className="me-2"
                >
                  Cancel
                </Button>
                <AddSubmitButton form={filesForm} isLoading={isLoading} />
              </Row>
            </Form.Item>
          </Form>
        </div>
      </Modal>
      {contextHolder}
    </Navbar>
  );
}
